import { useState, useEffect } from 'react';
import { useIsRTL } from '../../locale';

export interface SliderTrackClientBounds {
  getSize(): { width: number; height: number } | undefined;
}

export interface SliderDragHookProps {
  clientBounds: SliderTrackClientBounds;
  orientation: 'vertical' | 'horizontal';
  minPosition: number;
  maxPosition: number;

  onChange?: (event: { position: number; didDrag?: boolean }) => void;
  onCommit?: (event: { position: number; didDrag?: boolean }) => void;
}

export function useSliderDrag(
  props: Readonly<SliderDragHookProps>
): {
  /**
   * Start a drag operation.
   *
   * @param dragStartClientPos The [clientX, clientY] the drag started on.
   * @param dragStartPos The position (0 to 1) the grip started on.
   */
  startDrag(dragStartClientPos: [number, number], dragStartPos: number): void;
  abortDrag(): void;
  lastDragStartPos: number;
  didDrag: boolean;
} {
  const [isDragging, setIsDragging] = useState(false);
  const [didDrag, setDidDrag] = useState(false);
  const [[startClientX, startClientY], setDragStartClientPos] = useState([NaN, NaN]);
  const [lastDragStartPos, setLastDragStartPos] = useState(NaN);

  const isRTL = useIsRTL();

  useEffect(() => {
    if (!isDragging) return;

    function dragPos(clientX: number, clientY: number): number | undefined {
      const boundsSize = props.clientBounds.getSize();
      if (!boundsSize) return undefined;

      let pos = 0;
      if (props.orientation === 'vertical') {
        pos = (clientY - startClientY) / boundsSize.height;
        pos *= -1;
      } else {
        pos = (clientX - startClientX) / boundsSize.width;
        if (isRTL) {
          pos *= -1;
        }
      }

      pos += lastDragStartPos;

      if (pos < props.minPosition) {
        pos = props.minPosition;
      } else if (pos > props.maxPosition) {
        pos = props.maxPosition;
      }

      return pos;
    }

    function onDrag(e: MouseEvent) {
      if (!props.onChange) return;

      const position = dragPos(e.clientX, e.clientY);
      let gripDidDrag = false;
      if (position !== lastDragStartPos) {
        gripDidDrag = true;
        setDidDrag(true);
      }

      if (position !== undefined) {
        props.onChange.call(undefined, { position, didDrag: gripDidDrag });
      }
    }
    function onDragEnd(e: MouseEvent) {
      // only respond to left buttons being lifted
      if (e.button !== 0) return;
      setIsDragging(false);

      const position = dragPos(e.clientX, e.clientY);
      let gripDidDrag = false;
      if (position !== lastDragStartPos) {
        gripDidDrag = true;
        setDidDrag(true);
      }

      if (position !== undefined) {
        if (props.onChange) props.onChange.call(undefined, { position, didDrag: gripDidDrag });
        if (props.onCommit) props.onCommit.call(undefined, { position, didDrag: gripDidDrag });
      }
    }

    window.addEventListener('mousemove', onDrag);
    window.addEventListener('mouseup', onDragEnd);

    return () => {
      window.removeEventListener('mousemove', onDrag);
      window.removeEventListener('mouseup', onDragEnd);
    };
  });

  return {
    startDrag(dragStartClientPos, dragStartPos) {
      setDragStartClientPos(dragStartClientPos);
      setLastDragStartPos(dragStartPos);
      setDidDrag(false);
      setIsDragging(true);
    },
    abortDrag() {
      setIsDragging(false);
    },
    lastDragStartPos,
    didDrag,
  };
}
